import { apiClient } from '@app/api';
import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { blinkerAnimation } from '@app/core/ui/animations';
import { useYoutubeStatsEnabled } from '@app/modules/audit/hooks/useYoutubeStatsEnabled';
import { date, formatThousands, now, toTimestamp } from '@mi-front/core';
import { LocaleRelativeTime } from '@mi-front/ui';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { Chip, Tooltip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

export function YoutubeLiveCounter() {
  const station = useActiveStation();
  const youtubeStatsEnabled = useYoutubeStatsEnabled(station);

  const { data: youtubeStats, isLoading } = useQuery({
    queryKey: ['youtube-live-stats', station.id],
    queryFn: () =>
      apiClient.audit.fetchYoutubeStats(station.youtubeChannelId!, {
        from: toTimestamp(date().subtract(2, 'minutes').toDate()),
        to: toTimestamp(now()),
      }),
    enabled: youtubeStatsEnabled,
    refetchInterval: 60000,
  });

  const data = useMemo(() => {
    return !isLoading && youtubeStats && youtubeStats?.[youtubeStats.length - 1]?.channelId
      ? {
          viewers: youtubeStats?.[youtubeStats.length - 1].channelId[0][station.youtubeChannelId!],
          /** Raw time from API for relative display (e.g. "hace 2 minutos") */
          time: youtubeStats?.[youtubeStats.length - 1].time as string | number | null,
        }
      : {
          viewers: 'OFFLINE',
          time: null,
        };
  }, [isLoading, station.youtubeChannelId, youtubeStats]);

  return youtubeStatsEnabled && !isLoading ? (
    <Tooltip
      title={
        <>
          <FormattedMessage
            id="logger.streamScreen.controls.youtubeLiveViewers"
            defaultMessage="{count} espectadores en vivo"
            values={{
              count: typeof data.viewers === 'number' ? data.viewers.toLocaleString() : '—',
            }}
          />
          {data.time != null && (
            <div>
              <FormattedMessage
                id="logger.streamScreen.controls.updated"
                defaultMessage="Actualizado <time>{time}</time>"
                values={{
                  time: () => <LocaleRelativeTime value={data.time!} />,
                }}
              />
            </div>
          )}
        </>
      }
      placement="top"
    >
      <Chip
        sx={{
          paddingLeft: 0.5,
          opacity: data.viewers === 'OFFLINE' ? 0.5 : 1,
          cursor: 'default',
          '& .MuiChip-label': {
            cursor: 'default',
            userSelect: 'none',
          },
        }}
        icon={
          <YouTubeIcon
            color="error"
            sx={{
              color: data.viewers === 'OFFLINE' ? '#888' : 'red',
              animation: blinkerAnimation,
            }}
          />
        }
        variant="outlined"
        label={formatThousands(data.viewers)}
        // size="small"
      />
    </Tooltip>
  ) : null;
}
